import {
  GIF_EDIT_MAX_ADJUSTMENT,
  GIF_EDIT_MAX_FILTER_INTENSITY,
  GIF_EDIT_MIN_ADJUSTMENT,
  GIF_EDIT_MIN_FILTER_INTENSITY,
  getGifEditAppearanceColorMatrix,
  isIdentityGifEditAppearance,
} from '@buffer-mono/gif-toolkit'
import type {
  GifEditAppearance,
  GifEditColorMatrix,
  GifEditFilter,
} from '@buffer-mono/gif-toolkit'

export type ImageFilterPreset = GifEditFilter

export type ImageAppearance = Readonly<{
  brightness: number
  contrast: number
  saturation: number
  warmth: number
  filterPreset: ImageFilterPreset
  filterIntensity: number
}>

export type ImageColorMatrix = GifEditColorMatrix

export const IMAGE_EDITOR_MIN_ADJUSTMENT = GIF_EDIT_MIN_ADJUSTMENT
export const IMAGE_EDITOR_MAX_ADJUSTMENT = GIF_EDIT_MAX_ADJUSTMENT
export const IMAGE_EDITOR_MIN_FILTER_INTENSITY = GIF_EDIT_MIN_FILTER_INTENSITY
export const IMAGE_EDITOR_MAX_FILTER_INTENSITY = GIF_EDIT_MAX_FILTER_INTENSITY

const toGifEditAppearance = (
  appearance: ImageAppearance,
): GifEditAppearance => ({
  brightness: appearance.brightness,
  contrast: appearance.contrast,
  saturation: appearance.saturation,
  warmth: appearance.warmth,
  filter: appearance.filterPreset,
  filterIntensity: appearance.filterIntensity,
})

export const isIdentityImageAppearance = (
  appearance: ImageAppearance,
): boolean => isIdentityGifEditAppearance(toGifEditAppearance(appearance))

export const getImageAppearanceColorMatrix = (
  appearance: ImageAppearance,
): ImageColorMatrix =>
  getGifEditAppearanceColorMatrix(toGifEditAppearance(appearance))

/**
 * Serializes the appearance matrix for an SVG feColorMatrix element, whose
 * channel offsets are normalized to 0..1 rather than the 0..255 RGBA space
 * used by the shared matrix.
 */
export const getImageAppearanceSvgMatrixValues = (
  appearance: ImageAppearance,
): string =>
  getImageAppearanceColorMatrix(appearance)
    .flatMap((row) => [row[0], row[1], row[2], row[3], row[4] / 255])
    .join(' ')

export const getImageAppearanceCssFilter = (
  appearance: ImageAppearance,
  filterId: string,
): string =>
  isIdentityImageAppearance(appearance) ? 'none' : `url(#${filterId})`

export const applyImageAppearanceToRgba = (
  pixels: Uint8ClampedArray,
  appearance: ImageAppearance,
): boolean => {
  if (isIdentityImageAppearance(appearance)) return false

  const matrix = getImageAppearanceColorMatrix(appearance)

  for (let offset = 0; offset < pixels.length; offset += 4) {
    const red = pixels[offset]
    const green = pixels[offset + 1]
    const blue = pixels[offset + 2]
    const alpha = pixels[offset + 3]

    pixels[offset] =
      matrix[0][0] * red +
      matrix[0][1] * green +
      matrix[0][2] * blue +
      matrix[0][3] * alpha +
      matrix[0][4]
    pixels[offset + 1] =
      matrix[1][0] * red +
      matrix[1][1] * green +
      matrix[1][2] * blue +
      matrix[1][3] * alpha +
      matrix[1][4]
    pixels[offset + 2] =
      matrix[2][0] * red +
      matrix[2][1] * green +
      matrix[2][2] * blue +
      matrix[2][3] * alpha +
      matrix[2][4]
  }

  return true
}
